// EditProfile.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "./config";

const EditProfile = () => {
  const [profilePic, setProfilePic] = useState(null);
  const [currentProfilePic, setCurrentProfilePic] = useState("");
  const [fullName, setFullName] = useState("");
  const [displayUsername, setDisplayUsername] = useState("");
  const [tagline, setTagline] = useState("");
  const [bio, setBio] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [city, setCity] = useState("");
  const [age, setAge] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [usernameMessage, setUsernameMessage] = useState("");
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [usernameTimeout, setUsernameTimeout] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_BASE_URL}/profile`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      // Set existing profile data
      setCurrentProfilePic(res.data.profilePic || "");
      setFullName(res.data.fullName || "");
      setDisplayUsername(res.data.displayUsername || "");
      setTagline(res.data.tagline || "");
      setBio(res.data.bio || "");
      setCity(res.data.city || "");
      setZipCode(res.data.zipCode || "");
      setAge(res.data.age || "");
      setLoading(false);
    } catch (err) {
      console.error("Error fetching profile:", err);
      setMessage("Error loading profile data");
      setLoading(false);
    }
  };

  const handleProfilePicChange = (e) => {
    setProfilePic(e.target.files[0]);
  };

  const checkUsernameAvailability = async (username) => {
    if (!username || username.length < 3) {
      setUsernameAvailable(null);
      setUsernameMessage("");
      return;
    }

    setCheckingUsername(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        `${API_BASE_URL}/api/check-username/${username}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setUsernameAvailable(res.data.available);
      setUsernameMessage(res.data.message);
    } catch (err) {
      setUsernameAvailable(false);
      setUsernameMessage(err.response?.data?.message || "Error checking username");
    } finally {
      setCheckingUsername(false);
    }
  };

  const handleUsernameChange = (e) => {
    const username = e.target.value;
    setDisplayUsername(username);
    
    // Clear previous timeout
    if (usernameTimeout) {
      clearTimeout(usernameTimeout);
    }
    
    // Set new timeout for checking availability
    const timeout = setTimeout(() => {
      checkUsernameAvailability(username);
    }, 500); // Check after 500ms of no typing
    
    setUsernameTimeout(timeout);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    
    // Only append profile pic if a new one was selected
    if (profilePic) {
      formData.append("profilePic", profilePic);
    }
    
    formData.append("fullName", fullName);
    formData.append("displayUsername", displayUsername);
    formData.append("tagline", tagline);
    formData.append("bio", bio);
    formData.append("zipCode", zipCode);
    formData.append("city", city);
    formData.append("age", age);

    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API_BASE_URL}/profile`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          "Authorization": `Bearer ${token}`,
        },
      });

      setMessage("Profile updated successfully!");
      
      // Redirect to profile page after a brief delay
      setTimeout(() => {
        navigate("/profile");
      }, 1500);
    } catch (err) {
      setMessage("Error updating profile. Please try again.");
    }
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="row justify-content-center">
        <div className="col-md-6">
          <div className="card">
            <div className="card-body">
              <h2 className="card-title mb-4">Edit Profile</h2>
              
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label className="form-label">Current Profile Picture</label>
                  {currentProfilePic ? (
                    <div className="mb-2">
                      <img
                        src={`${API_BASE_URL}/${currentProfilePic}`}
                        alt="Current Profile"
                        style={{ 
                          width: "150px", 
                          height: "150px", 
                          borderRadius: "50%",
                          objectFit: "cover",
                          display: "block",
                          marginBottom: "10px"
                        }}
                      />
                    </div>
                  ) : (
                    <p className="text-muted">No profile picture uploaded</p>
                  )}
                  
                  <label className="form-label">Upload New Profile Picture (optional)</label>
                  <input 
                    type="file" 
                    className="form-control" 
                    onChange={handleProfilePicChange}
                    accept="image/*"
                  />
                  <div className="form-text">Leave empty to keep current picture</div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Full Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Enter your full name"
                    maxLength="100"
                  />
                  <div className="form-text">Your full name as you'd like it displayed</div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Username</label>
                  <div className="input-group">
                    <span className="input-group-text">@</span>
                    <input
                      type="text"
                      className={`form-control ${
                        displayUsername.length >= 3 && usernameAvailable !== null
                          ? usernameAvailable
                            ? "is-valid"
                            : "is-invalid"
                          : ""
                      }`}
                      value={displayUsername}
                      onChange={handleUsernameChange}
                      placeholder="Choose a unique username"
                      maxLength="20"
                      pattern="[a-zA-Z0-9_\-]+"
                    />
                    {checkingUsername && (
                      <span className="input-group-text">
                        <span className="spinner-border spinner-border-sm" role="status">
                          <span className="visually-hidden">Checking...</span>
                        </span>
                      </span>
                    )}
                  </div>
                  {usernameMessage && (
                    <div className={`form-text ${usernameAvailable ? "text-success" : "text-danger"}`}>
                      {usernameMessage}
                    </div>
                  )}
                  <div className="form-text">
                    3-20 characters, letters, numbers, underscores, and dashes only
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Tagline</label>
                  <input
                    type="text"
                    className="form-control"
                    value={tagline}
                    onChange={(e) => setTagline(e.target.value)}
                    placeholder="Enter your tagline"
                    maxLength="100"
                  />
                  <div className="form-text">A short description about yourself</div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Bio</label>
                  <textarea
                    className="form-control"
                    value={bio}
                    onChange={(e) => setBio(e.target.value)}
                    placeholder="Write something about yourself"
                    rows="4"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">City</label>
                  <input
                    type="text"
                    className="form-control"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="Enter your city"
                  />
                  <div className="form-text">Your city helps you connect with local members</div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Zip Code</label>
                  <input
                    type="text"
                    className="form-control"
                    value={zipCode}
                    onChange={(e) => setZipCode(e.target.value)}
                    placeholder="Enter your zip code"
                    pattern="[0-9]{5}"
                    maxLength="5"
                  />
                  <div className="form-text">5-digit zip code for finding nearby members</div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Age</label>
                  <input
                    type="number"
                    className="form-control"
                    value={age}
                    onChange={(e) => setAge(e.target.value)}
                    placeholder="Enter your age"
                    min="1"
                    max="120"
                  />
                  <div className="form-text">Your age helps connect you with peers</div>
                </div>

                {message && (
                  <div className={`alert ${message.includes("successfully") ? "alert-success" : "alert-danger"} mb-3`}>
                    {message}
                  </div>
                )}

                <div className="d-grid gap-2">
                  <button type="submit" className="btn btn-primary">
                    Save Changes
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-outline-secondary"
                    onClick={() => navigate("/profile")}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditProfile;